import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';

import Icon, { IconEnum } from 'components/common/Icon';

import TextInput from './TextInput';

const Container = styled.span`
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-start;

    input {
        caret-color: transparent;
    }

    input:focus {
        cursor: pointer;
    }
`;

const AutoCompleteSuggestions = styled.div`
    position: absolute;
    z-index: 1;
    top: 95px;
    display: flex;
    flex-direction: column;
    background: var(--color-white);
    left: 0;
    width: 100%;
    min-width: fit-content;
    max-height: 330px;
    overflow-y: auto;
    padding: 10px;
    gap: 6px;
    border-radius: 10px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid var(--color-primary);

    &.hidden {
        display: none;
    }
`;

const Suggestion = styled.label<{ darkMode?: boolean }>`
    font-weight: 500;
    font-size: 13px;
    line-height: 36px;
    color: var(--primary-color);
    background: var(--color-alt-second);
    border-radius: 10px;
    padding: 7px 17px 7px 7px;
    border: 1px solid transparent;
    transition: border 0.1s ease-in-out;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    div {
        display: flex;
        flex-direction: row;
        align-items: center;
        width: 100%;
        gap: 10px;
    }

    input {
        --checkbox-background-color: transparent;
        cursor: pointer;
        pointer-events: none;
    }

    input[type="checkbox"]:checked {
        --checkbox-color: var(--color-white);
    }

    &:hover, &:focus {
        transition: border 0.1s ease-in-out;
        border: 1px solid var(--color-alt-hover);
    }

    &.active {
        background: ${(props) => props.darkMode ? 'var(--color-primary)' : 'var(--color-alt)'};
        color: var(--color-white);

        div, span {
            color: var(--color-white);
        }

        img {
            filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%);
        }
    }
`;

const LabelWrapper = styled.span`
    display: flex;
    flex-direction: column;
    line-height: normal;
    gap: 2px;
`;

const HelperText = styled.span`
    font-size: 11px;
    font-weight: 400;
    opacity: 0.65;
    line-height: 14px;
`;

const Action = styled.div`
    position: absolute;
    right: 10px;
    background: var(--color-white);
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    pointer-events: none;
    cursor: pointer;
`;

export interface BooleanCheckboxItem {
    label: string;
    fieldName: string;
    value: boolean;
    helperText?: string;
}

interface BooleanMultiSelectInputProps {
    items: BooleanCheckboxItem[];
    placeholder?: string;
    onChange: (fieldName: string, value: boolean) => void;
    inline?: boolean;
}

const BooleanMultiSelectInput: FC<BooleanMultiSelectInputProps> = ({ items, placeholder, onChange, inline }) => {
    const { user } = useUser();
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<HTMLDivElement>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const displayValue = inline ? '' : items.filter((x) => x.value).map((x) => x.label).join(', ');

    const handleToggle = (e: React.MouseEvent<HTMLLabelElement>, item: BooleanCheckboxItem) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(item.fieldName, !item.value);

        if (!inline) {
            inputRef.current?.focus();
            setTimeout(() => setShowSuggestions(true), 200);
        }
    };

    const handleClick = () => {
        if (showSuggestions) {
            setTimeout(() => setShowSuggestions(false), 200);

            return;
        }

        setShowSuggestions(true);
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (
            autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node) &&
            inputRef.current && !inputRef.current.contains(e.target as Node)
        ) {
            setShowSuggestions(false);
        }
    };

    useEffect(() => {
        if (inline) return;

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const renderItem = (item: BooleanCheckboxItem) => <Suggestion
        key={item.fieldName}
        darkMode={user.isDarkMode}
        onClick={(e: React.MouseEvent<HTMLLabelElement>) => handleToggle(e, item)}
        className={clsx({ active: item.value })}
    >
        <div>
            {item.helperText
                ? <LabelWrapper><span>{item.label}</span><HelperText>{item.helperText}</HelperText></LabelWrapper>
                : item.label
            }
        </div>
        <input readOnly checked={item.value} type="checkbox"/>
    </Suggestion>;

    if (inline) {
        return <>{items.map(renderItem)}</>;
    }

    return (
        <Container>
            <TextInput
                ref={inputRef}
                placeholder={placeholder}
                value={displayValue}
                onClick={handleClick}
                readOnly
            />
            <Action>
                {showSuggestions ? <Icon icon={IconEnum.ARROW_UP}/> : <Icon icon={IconEnum.ARROW_DOWN}/>}
            </Action>
            <AutoCompleteSuggestions
                className={clsx({ hidden: !showSuggestions })}
                ref={autocompleteRef}
            >
                {items.map(renderItem)}
            </AutoCompleteSuggestions>
        </Container>
    );
};

export default BooleanMultiSelectInput;
